import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { User } from '../../types/user';
import { getCurrentUserCredits } from '../../services/CreditsService';
import { getScheduledCampaigns, ScheduledCampaign, updateCampaign, getCampaignRecipients, getSmsActivity, SmsActivityPoint, getDashboardStats, DashboardStats } from '../../services/SmsService';
import BaseChart from '../charts/BaseChart';
import { getUserSenderIds, SenderIdDto } from '../../services/SenderIdService';

const getStartOfWeek = (date: Date): Date => {
  const start = new Date(date);
  const dayOfWeek = start.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  start.setDate(start.getDate() + daysToMonday);
  start.setHours(0, 0, 0, 0);
  return start;
};

const getCurrentUserIdFromStorage = (): string | null => {
  try {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('accessToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.id || null;
  } catch (_) {
    return null;
  }
};

const isSameDay = (dateA: Date, dateB: Date): boolean =>
  dateA.getFullYear() === dateB.getFullYear() &&
  dateA.getMonth() === dateB.getMonth() &&
  dateA.getDate() === dateB.getDate();

const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type ActivityGrouping = 'hour' | 'day' | 'week' | 'month';

const formatDateTimeForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

interface DashboardProps {
  user: User;
}

const HelpSection = () => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex flex-col items-center text-center">
        <div className="mb-4">
          <svg 
            className="w-16 h-16 text-purple-600" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" 
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium mb-2">Need help?</h3>
        <p className="text-gray-600 text-sm mb-4">
          Browse our knowledge base and get support from our Service HuB
        </p>
        <p className="text-gray-400 text-sm font-medium">
          Support portal is temporarily unavailable
        </p>
      </div>
    </div>
  );
};

export const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [remainingCredits, setRemainingCredits] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => getStartOfWeek(new Date()));
  const [scheduledCampaigns, setScheduledCampaigns] = useState<ScheduledCampaign[]>([]);
  const [isLoadingScheduled, setIsLoadingScheduled] = useState(true);
  const isInitialLoadRef = React.useRef(true);
  const [activityData, setActivityData] = useState<SmsActivityPoint[]>([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [activityGroupBy, setActivityGroupBy] = useState<ActivityGrouping>('day');
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    inProgress: 0,
    sent: 0,
    failed: 0,
    scheduled: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<ScheduledCampaign | null>(null);
  const [editFormData, setEditFormData] = useState({
    campaignName: '',
    scheduledDate: '',
    scheduledTime: '',
    body: '',
    senderId: '',
    recipients: [] as string[]
  });
  const [availableSenderIds, setAvailableSenderIds] = useState<SenderIdDto[]>([]);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const buildActivityRequestParams = useCallback(() => {
    const now = new Date();
    let groupBy: ActivityGrouping = 'day';

    let start: Date | undefined;
    let end: Date | undefined = new Date(now);

    switch (selectedTimeRange) {
      case '24h': {
        groupBy = 'hour';
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      }
      case '7d': {
        groupBy = 'day';
        start = new Date(now);
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      }
      case '30d': {
        groupBy = 'day';
        start = new Date(now);
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      }
      case 'custom':
      default: {
        groupBy = 'day';
        start = new Date(now);
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      }
    }

    return {
      startDate: start ? formatDateTimeForApi(start) : undefined,
      endDate: end ? formatDateTimeForApi(end) : undefined,
      groupBy
    };
  }, [selectedTimeRange]);

  const fetchActivityData = useCallback(async () => {
    if (!user) {
      setActivityData([]);
      return;
    }

    const { startDate, endDate, groupBy } = buildActivityRequestParams();
    setIsLoadingActivity(true);
    setActivityError(null);
    setActivityGroupBy(groupBy);

    try {
      const data = await getSmsActivity({ startDate, endDate, groupBy });
      setActivityData(data);
    } catch (error) {
      console.error('Failed to fetch SMS activity:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to load SMS activity';
      setActivityError(message);
      setActivityData([]);
    } finally {
      setIsLoadingActivity(false);
    }
  }, [user, buildActivityRequestParams]);

  const formatActivityLabel = useCallback(
    (period: string) => {
      if (!period) return '';

      if (activityGroupBy === 'hour' || activityGroupBy === 'day') {
        const normalized = period.replace(' ', 'T');
        const parsedDate = new Date(normalized);
        if (!Number.isNaN(parsedDate.getTime())) {
          if (activityGroupBy === 'hour') {
            return new Intl.DateTimeFormat(undefined, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric'
            }).format(parsedDate);
          }

          return new Intl.DateTimeFormat(undefined, {
            month: 'short',
            day: 'numeric'
          }).format(parsedDate);
        }
      }

      if (activityGroupBy === 'month') {
        const normalized = `${period}-01T00:00:00`;
        const parsedDate = new Date(normalized);
        if (!Number.isNaN(parsedDate.getTime())) {
          return new Intl.DateTimeFormat(undefined, {
            month: 'short',
            year: 'numeric'
          }).format(parsedDate);
        }
      }

      return period;
    },
    [activityGroupBy]
  );

  const activityChartData = useMemo(() => {
    if (!activityData.length) {
      return null;
    }

    const labels = activityData.map((point) => formatActivityLabel(point.period));
    const datasetConfigs: Record<
      string,
      { label: string; key: keyof SmsActivityPoint; borderColor: string; backgroundColor: string }
    > = {
      all: {
        label: 'Total messages',
        key: 'total',
        borderColor: '#FF5722',
        backgroundColor: 'rgba(255, 87, 34, 0.15)'
      },
      sent: {
        label: 'Sent messages',
        key: 'sent',
        borderColor: '#2563EB',
        backgroundColor: 'rgba(37, 99, 235, 0.15)'
      },
      failed: {
        label: 'Failed messages',
        key: 'failed',
        borderColor: '#DC2626',
        backgroundColor: 'rgba(220, 38, 38, 0.15)'
      },
      scheduled: {
        label: 'Queued messages',
        key: 'queued',
        borderColor: '#7C3AED',
        backgroundColor: 'rgba(124, 58, 237, 0.15)'
      }
    };

    const config = datasetConfigs[selectedFilter] ?? datasetConfigs.all;
    const values = activityData.map((point) => Number(point[config.key] ?? 0));

    return {
      labels,
      datasets: [
        {
          label: config.label,
          data: values,
          borderColor: config.borderColor,
          backgroundColor: config.backgroundColor,
          borderWidth: 2,
          tension: 0.3,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 4
        }
      ]
    };
  }, [activityData, selectedFilter, formatActivityLabel]);

  const hasActivityValues = useMemo(
    () =>
      activityChartData?.datasets.some((dataset) =>
        dataset.data.some((value) => value > 0)
      ) ?? false,
    [activityChartData]
  );

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setRemainingCredits(null);
      setIsLoadingCredits(false);
      return;
    }

    try {
      if (isInitialLoadRef.current) {
        setIsLoadingCredits(true);
      }
      const credits = await getCurrentUserCredits();
      setRemainingCredits(credits.remaining);
      isInitialLoadRef.current = false;
    } catch (error) {
      console.error('Failed to fetch user credits:', error);
      if (isInitialLoadRef.current) {
        setRemainingCredits(null);
        isInitialLoadRef.current = false;
      }
    } finally {
      setIsLoadingCredits(false);
    }
  }, [user]);

  const fetchDashboardStats = useCallback(async () => {
    if (!user) {
      setDashboardStats({ inProgress: 0, sent: 0, failed: 0, scheduled: 0 });
      setIsLoadingStats(false);
      return;
    }

    try {
      setIsLoadingStats(true);
      const stats = await getDashboardStats();
      setDashboardStats(stats);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      setDashboardStats({ inProgress: 0, sent: 0, failed: 0, scheduled: 0 });
    } finally {
      setIsLoadingStats(false);
    }
  }, [user]);

  useEffect(() => {
    isInitialLoadRef.current = true;
    fetchCredits();
  }, [user, fetchCredits]);

  useEffect(() => {
    if (!user) return;
    setCurrentWeekStart(getStartOfWeek(new Date()));
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const intervalId = setInterval(() => {
      fetchCredits();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [user, fetchCredits]);

  useEffect(() => {
    if (!user) return;

    const handleFocus = () => {
      fetchCredits();
      fetchDashboardStats();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, fetchCredits, fetchDashboardStats]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  useEffect(() => {
    if (!user) return;

    const intervalId = setInterval(() => {
      fetchDashboardStats();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [user, fetchDashboardStats]);

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  // Fetch scheduled campaigns for the selected week
  useEffect(() => {
    const fetchScheduledCampaigns = async () => {
      if (!user) {
        setIsLoadingScheduled(false);
        return;
      }

      try {
        setIsLoadingScheduled(true);

        const startOfWeek = new Date(currentWeekStart);
        const endOfWeek = new Date(currentWeekStart);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        const campaigns = await getScheduledCampaigns({
          startDate: formatDateForApi(startOfWeek),
          endDate: formatDateForApi(endOfWeek)
        });
        setScheduledCampaigns(campaigns);
      } catch (error) {
        console.error('Failed to fetch scheduled campaigns:', error);
        setScheduledCampaigns([]);
      } finally {
        setIsLoadingScheduled(false);
      }
    };

    fetchScheduledCampaigns();
  }, [user, currentWeekStart]);

  // Load sender IDs when modal opens
  useEffect(() => {
    const loadSenderIds = async () => {
      if (!user || !isEditModalOpen) return;
      try {
        const uid = getCurrentUserIdFromStorage();
        if (uid) {
          const list = await getUserSenderIds(uid);
          setAvailableSenderIds(list);
        }
      } catch (error) {
        console.error('Failed to load sender IDs:', error);
      }
    };
    loadSenderIds();
  }, [user, isEditModalOpen]);

  // Handle campaign click to open edit modal
  const handleCampaignClick = async (campaign: ScheduledCampaign) => {
    setEditingCampaign(campaign);
    setEditFormData({
      campaignName: campaign.campaignName || '',
      scheduledDate: campaign.scheduledDate || '',
      scheduledTime: campaign.scheduledTime || '',
      body: campaign.body || '',
      senderId: campaign.senderId || '',
      recipients: []
    });
    setIsEditModalOpen(true);
    setIsLoadingRecipients(true);
    
    try {
      const recipients = await getCampaignRecipients(campaign.id);
      setEditFormData(prev => ({ ...prev, recipients }));
    } catch (error) {
      console.error('Failed to load recipients:', error);
    } finally {
      setIsLoadingRecipients(false);
    }
  };

  // Handle save campaign
  const handleSaveCampaign = async () => {
    if (!editingCampaign) return;
    
    if (!editFormData.body.trim()) {
      alert('Message body is required');
      return;
    }
    
    if (editFormData.recipients.length === 0) {
      alert('At least one recipient is required');
      return;
    }

    setIsSaving(true);
    try {
      await updateCampaign(editingCampaign.id, {
        campaignName: editFormData.campaignName || undefined,
        scheduledDate: editFormData.scheduledDate || undefined,
        scheduledTime: editFormData.scheduledTime || undefined,
        body: editFormData.body,
        senderId: editFormData.senderId || undefined,
        recipients: editFormData.recipients
      });
      
      // Refresh campaigns list for current week selection
      const startOfWeek = new Date(currentWeekStart);
      const endOfWeek = new Date(currentWeekStart);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      const campaigns = await getScheduledCampaigns({
        startDate: formatDateForApi(startOfWeek),
        endDate: formatDateForApi(endOfWeek)
      });
      setScheduledCampaigns(campaigns);
      
      setIsEditModalOpen(false);
      setEditingCampaign(null);
    } catch (error: any) {
      alert(error?.message || 'Failed to update campaign');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to get day of week (0 = Sunday, 1 = Monday, etc.)
  const getDayOfWeek = (dateString: string): number => {
    // Parse date string (YYYY-MM-DD) in local time to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    return date.getDay();
  };

  // Helper function to convert time string (HH:MM) to hour decimal
  const timeToHour = (timeString: string | null): number => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours + minutes / 60;
  };

  // Helper function to format time for display
  const formatTime = (timeString: string | null): string => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
  };

  // Group campaigns by day of week (1 = Monday, 7 = Sunday)
  const campaignsByDay: Record<number, ScheduledCampaign[]> = {
    1: [], // Monday
    2: [], // Tuesday
    3: [], // Wednesday
    4: [], // Thursday
    5: [], // Friday
    6: [], // Saturday
    0: []  // Sunday
  };

  const weekDays = useMemo(() => {
    const dayOrder: Array<{ index: number; offset: number }> = [
      { index: 1, offset: 0 }, // Monday
      { index: 2, offset: 1 },
      { index: 3, offset: 2 },
      { index: 4, offset: 3 },
      { index: 5, offset: 4 },
      { index: 6, offset: 5 },
      { index: 0, offset: 6 }  // Sunday
    ];
    const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const today = new Date();

    return dayOrder.map(({ index, offset }) => {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + offset);

      return {
        dayIndex: index,
        label: dayNames[index],
        dateLabel: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        isToday: isSameDay(date, today)
      };
    });
  }, [currentWeekStart]);

  const weekRangeLabel = useMemo(() => {
    const start = new Date(currentWeekStart);
    const end = new Date(currentWeekStart);
    end.setDate(end.getDate() + 6);

    const sameYear = start.getFullYear() === end.getFullYear();

    const startLabel = start.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' })
    });

    const endLabel = end.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    return `${startLabel} - ${endLabel}`;
  }, [currentWeekStart]);

  const goToPreviousWeek = useCallback(() => {
    setCurrentWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return getStartOfWeek(newDate);
    });
  }, []);

  const goToNextWeek = useCallback(() => {
    setCurrentWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return getStartOfWeek(newDate);
    });
  }, []);

  const goToCurrentWeek = useCallback(() => {
    setCurrentWeekStart(getStartOfWeek(new Date()));
  }, []);

  scheduledCampaigns.forEach(campaign => {
    if (campaign.scheduledDate) {
      const dayOfWeek = getDayOfWeek(campaign.scheduledDate);
      // Convert Sunday (0) to 7 for our mapping
      const dayKey = dayOfWeek === 0 ? 0 : dayOfWeek;
      if (campaignsByDay[dayKey]) {
        campaignsByDay[dayKey].push(campaign);
      }
    }
  });

  // Sort campaigns by time within each day
  Object.keys(campaignsByDay).forEach(day => {
    campaignsByDay[parseInt(day)].sort((a, b) => {
      const timeA = timeToHour(a.scheduledTime);
      const timeB = timeToHour(b.scheduledTime);
      return timeA - timeB;
    });
  });

  // Render schedule blocks for a day
  const renderDaySchedule = (dayIndex: number, dayLabel: string, dateLabel: string, isToday: boolean) => {
    const campaigns = campaignsByDay[dayIndex];
    
    if (isLoadingScheduled) {
      return (
        <div className="flex items-center">
          <div className="w-24 text-sm font-medium flex flex-col">
            <span className={isToday ? 'text-[#FF5722]' : 'text-gray-900'}>{dayLabel}</span>
            <span className={`text-xs ${isToday ? 'text-[#FF5722]' : 'text-gray-500'}`}>{dateLabel}</span>
          </div>
          <div className="flex-1 flex items-center justify-center py-2">
            <span className="text-xs text-gray-400">Loading...</span>
          </div>
        </div>
      );
    }

    if (campaigns.length === 0) {
      return (
        <div className="flex items-center">
          <div className="w-24 text-sm font-medium flex flex-col">
            <span className={isToday ? 'text-[#FF5722]' : 'text-gray-900'}>{dayLabel}</span>
            <span className={`text-xs ${isToday ? 'text-[#FF5722]' : 'text-gray-500'}`}>{dateLabel}</span>
          </div>
          <div className="flex-1 flex">
            <div className="text-xs text-gray-500 px-2 py-1">No scheduled campaigns</div>
          </div>
        </div>
      );
    }

    // Group campaigns that are close together (within 1 hour) to show as blocks
    const blocks: Array<{ start: number; end: number; campaigns: ScheduledCampaign[] }> = [];
    
    campaigns.forEach(campaign => {
      const startHour = timeToHour(campaign.scheduledTime);
      // Assume each campaign takes about 1 hour window (can be adjusted)
      const endHour = startHour + 1;
      
      // Check if this campaign can be merged with an existing block
      let merged = false;
      for (const block of blocks) {
        if (startHour >= block.start - 1 && startHour <= block.end + 1) {
          block.end = Math.max(block.end, endHour);
          block.campaigns.push(campaign);
          merged = true;
          break;
        }
      }
      
      if (!merged) {
        blocks.push({ start: startHour, end: endHour, campaigns: [campaign] });
      }
    });

    // Sort blocks by start time
    blocks.sort((a, b) => a.start - b.start);

    return (
      <div className="flex items-center">
        <div className="w-24 text-sm font-medium flex flex-col">
          <span className={isToday ? 'text-[#FF5722]' : 'text-gray-900'}>{dayLabel}</span>
          <span className={`text-xs ${isToday ? 'text-[#FF5722]' : 'text-gray-500'}`}>{dateLabel}</span>
        </div>
        <div className="flex-1 flex relative" style={{ minHeight: '32px' }}>
          {blocks.map((block, idx) => {
            const startPercent = (block.start / 24) * 100;
            const widthPercent = ((block.end - block.start) / 24) * 100;
            const campaign = block.campaigns[0]; // Use first campaign for display
            const campaignName = campaign.campaignName || 'Untitled Campaign';
            const timeText = campaign.scheduledTime 
              ? `${formatTime(campaign.scheduledTime)}`
              : '';
            
            return (
              <div
                key={idx}
                onClick={() => handleCampaignClick(campaign)}
                className="bg-blue-100 rounded px-2 py-1 text-xs mx-1 absolute cursor-pointer hover:bg-blue-200 transition-colors"
                style={{
                  left: `${startPercent}%`,
                  width: `${widthPercent}%`,
                  minWidth: '80px'
                }}
                title={`${campaignName} - ${timeText} - ${campaign.recipientCount} recipient(s) - Click to edit`}
              >
                <div className="truncate font-medium">{campaignName}</div>
                <div className="text-gray-600">{timeText}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Stats Grid */}
      <div className="flex gap-6">
        {/* Left Side - Stats Boxes */}
        <div className="grid grid-cols-2 gap-3 w-fit self-center">
          {/* SMS in progress */}
          <div className="bg-[#FF5722] text-white p-4 rounded-lg w-[200px] h-[200px] hover:shadow-lg transition-shadow">
            <div className="flex flex-col h-full">
              <h3 className="font-bold mb-2">SMS in progress</h3>
              <div className="text-4xl font-bold mb-4">
                {isLoadingStats ? '...' : dashboardStats.inProgress.toLocaleString()}
              </div>
              <p className="text-sm mt-auto">Last 24 hours</p>
            </div>
          </div>

          {/* SMS Sent */}
          <div className="bg-white p-4 rounded-lg shadow w-[200px] h-[200px] hover:shadow-lg transition-shadow">
            <div className="flex flex-col h-full">
              <h3 className="font-bold mb-2">SMS sent</h3>
              <div className="text-4xl font-bold mb-4">
                {isLoadingStats ? '...' : dashboardStats.sent.toLocaleString()}
              </div>
              <p className="text-sm mt-auto">Last 24 hours</p>
            </div>
          </div>

          {/* SMS Failed */}
          <div className="bg-white p-4 rounded-lg shadow w-[200px] h-[200px] hover:shadow-lg transition-shadow">
            <div className="flex flex-col h-full">
              <h3 className="font-bold mb-2">SMS failed</h3>
              <div className="text-4xl font-bold mb-4">
                {isLoadingStats ? '...' : dashboardStats.failed.toLocaleString()}
              </div>
              <p className="text-sm mt-auto">Last 24 hours</p>
            </div>
          </div>

          {/* SMS Scheduled */}
          <div className="bg-white p-4 rounded-lg shadow w-[200px] h-[200px] hover:shadow-lg transition-shadow">
            <div className="flex flex-col h-full">
              <h3 className="font-bold mb-2">SMS scheduled</h3>
              <div className="text-4xl font-bold mb-4">
                {isLoadingStats ? '...' : dashboardStats.scheduled.toLocaleString()}
              </div>
              <p className="text-sm mt-auto">Next 24 hours</p>
            </div>
          </div>
        </div>

        {/* Right Side - Chart Section */}
        <div className="bg-white rounded-lg shadow-md p-6 w-[600px]">
          {/* Chart Header with Filters */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold">SMS Activity</h3>
            
            <div className="flex items-center space-x-4">
              {/* Time Range Filter */}
              <div className="flex items-center space-x-2">
                <select 
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  className="border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                >
                  <option value="24h">Last 24 hours</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="custom">Custom range</option>
                </select>
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <select 
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  className="border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                >
                  <option value="all">All SMS</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>

              {/* Export Button */}
              <button className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Chart Area */}
          <div className="h-[300px] border-t pt-4">
            {isLoadingActivity ? (
              <div className="flex h-full items-center justify-center text-gray-500">
                <span>Loading activity...</span>
              </div>
            ) : activityError ? (
              <div className="flex h-full items-center justify-center px-4 text-center text-sm text-red-500">
                <span>{activityError}</span>
              </div>
            ) : activityChartData && hasActivityValues ? (
              <div className="h-full">
                <BaseChart
                  type="line"
                  data={activityChartData}
                  height={260}
                  options={{
                    plugins: {
                      legend: {
                        display: false
                      }
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false
                        }
                      },
                      y: {
                        beginAtZero: true,
                        grid: {
                          display: true
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center text-center text-gray-500">
                <svg className="mb-4 h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p>No activity data available</p>
                <p className="mt-2 text-sm">Start sending SMS to see your activity chart</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {/* SMS Schedule Calendar */}
        <div className="bg-white p-6 rounded-lg shadow-md col-span-2">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold">SMS Schedule</h3>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 text-sm">
                <button
                  type="button"
                  onClick={goToPreviousWeek}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                  aria-label="Previous week"
                >
                  ‹
                </button>
                <div className="font-medium text-gray-700">{weekRangeLabel}</div>
                <button
                  type="button"
                  onClick={goToNextWeek}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                  aria-label="Next week"
                >
                  ›
                </button>
                <button
                  type="button"
                  onClick={goToCurrentWeek}
                  className="text-xs text-[#FF5722] hover:underline"
                >
                  This week
                </button>
              </div>

              {/* Tab Buttons */}
              <div className="flex text-sm">
                <button className="px-4 py-1 text-blue-600 border-b-2 border-blue-600">
                  Allowed times
                </button>
                <button className="px-4 py-1 text-gray-500 hover:text-gray-700">
                  Exceptions
                </button>
              </div>
            </div>
          </div>

          {/* Time Scale */}
          <div className="flex items-center text-xs text-gray-500 mb-4 border-b pb-2">
            <div className="w-24"></div> {/* Space for day labels */}
            {Array.from({ length: 24 }, (_, i) => (
              <div key={i} className="flex-1 text-center">
                {i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i-12}pm`}
              </div>
            ))}
          </div>

          {/* Schedule Grid */}
          <div className="space-y-2">
            {weekDays.map((day) => (
              <React.Fragment key={day.dayIndex}>
                {renderDaySchedule(day.dayIndex, day.label, day.dateLabel, day.isToday)}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Balance Card */}
        <div className="bg-white p-6 rounded-lg shadow md:col-span-1">
          <h3 className="font-bold mb-4">Your balance</h3>
          <div className="mb-4">
            <div className="text-sm font-normal text-gray-600">Available SMS credits</div>
            <div className="text-2xl font-bold">
              {isLoadingCredits
                ? '...'
                : remainingCredits !== null
                  ? remainingCredits.toLocaleString()
                  : '0'}
            </div>
          </div>
          <button 
            className="w-full border-2 border-[#FF5722] text-[#FF5722] py-2 rounded-md hover:bg-[#FF5722] hover:text-white transition-colors duration-200"
          >
            Top up
          </button>
        </div>

        {/* Help section */}
        <HelpSection />
      </div>

      {/* Edit Campaign Modal */}
      {isEditModalOpen && editingCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsEditModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Edit Campaign</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Campaign Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name
                </label>
                <input
                  type="text"
                  value={editFormData.campaignName}
                  onChange={(e) => setEditFormData({ ...editFormData, campaignName: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  placeholder="Enter campaign name"
                />
              </div>

              {/* Schedule Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scheduled Date
                  </label>
                  <input
                    type="date"
                    value={editFormData.scheduledDate}
                    onChange={(e) => setEditFormData({ ...editFormData, scheduledDate: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scheduled Time
                  </label>
                  <input
                    type="time"
                    value={editFormData.scheduledTime}
                    onChange={(e) => setEditFormData({ ...editFormData, scheduledTime: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Sender ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender ID
                </label>
                <select
                  value={editFormData.senderId}
                  onChange={(e) => setEditFormData({ ...editFormData, senderId: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                >
                  <option value="">Select sender ID</option>
                  {availableSenderIds.map(s => (
                    <option key={s.id} value={s.senderId}>{s.senderId}</option>
                  ))}
                </select>
              </div>

              {/* Message Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={editFormData.body}
                  onChange={(e) => setEditFormData({ ...editFormData, body: e.target.value })}
                  rows={6}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  placeholder="Enter your message"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {editFormData.body.length} characters
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipients {isLoadingRecipients && <span className="text-gray-500">(Loading...)</span>}
                </label>
                <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                  {editFormData.recipients.length === 0 ? (
                    <div className="text-sm text-gray-500">No recipients</div>
                  ) : (
                    <div className="space-y-1">
                      {editFormData.recipients.map((phone, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm">{phone}</span>
                          <button
                            onClick={() => {
                              setEditFormData({
                                ...editFormData,
                                recipients: editFormData.recipients.filter((_, i) => i !== idx)
                              });
                            }}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <input
                    type="text"
                    placeholder="Add phone number (e.g., +1234567890)"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = e.currentTarget;
                        const phone = input.value.trim();
                        if (phone && !editFormData.recipients.includes(phone)) {
                          setEditFormData({
                            ...editFormData,
                            recipients: [...editFormData.recipients, phone]
                          });
                          input.value = '';
                        }
                      }
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Press Enter to add recipient</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCampaign}
                  disabled={isSaving}
                  className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 